import React, { createContext, useContext, useMemo, useState } from "react";

// Idiomas suportados
export const LANG = {
  EN: "en",
  PT: "pt-PT",
};

// Dicionários (podes dividir por ficheiros/namespaces no futuro)
const DICTS = {
  [LANG.EN]: {
    nav: {
      random: "Create",
      tools: "Tools",
      picker: "Picker",
      scanner: "Scanner",
      load: "Import",
      palettes: "Palettes",
      converter: "Converter",
      gallery: "Gallery",
      collection: "Collection",
      suggestions: "Suggestions"
    },
    toolsMenu: {
      title: "Choose a Tool",
      subtitle: "Select which tool you want to use",
      picker: {
        title: "Color Picker",
        description: "Find markers by selecting a color from an image"
      },
      converter: {
        title: "Brand Converter",
        description: "Find similar colors across different brands"
      },
      hexFinder: {
        title: "Hex Finder",
        description: "Find the closest marker for any hex color value"
      }
    },
    hexFinder: {
      title: "Hex Finder",
      subtitle: "Enter any hex color and find the closest matching marker",
      brand: "Target Brand",
      hexLabel: "Hex Color",
      hexPlaceholder: "e.g. #A3C4E2 or A3C4E2",
      invalidHex: "Please enter a valid 6-digit hex color",
      algorithm: "Algorithm",
      searchButton: "Find Closest Match",
      searching: "Finding closest match...",
      noResults: "No matches found",
      yourColor: "Your Color",
      bestMatch: "Best Match",
      viewMore: "View More Matches",
      showLess: "Show Less",
      otherMatches: "Other Close Matches",
      tips: {
        title: "Tips",
        tip1: "Enter a hex value with or without the # prefix",
        tip2: "Results are sorted by color similarity using advanced color matching",
        tip3: "The top result is the closest matching marker in the selected brand",
        tip4: "Toggle between RGB (faster) and Delta E (perceptually accurate) algorithms to compare results"
      },
      help: {
        title: "How to Use Hex Finder",
        tip1: "Select the marker brand you want to search in",
        tip2: "Enter a hex color value (e.g. #FF5733 or FF5733)",
        tip3: "Click 'Find Closest Match' to see the nearest markers",
        tip4: "Results show similarity percentages — higher is a closer match"
      }
    },
    brandConverter: {
      badge: "Brand Converter",
      title: "Brand Converter",
      subtitle: "Find similar colors across different marker brands",
      inputBrand: "From Brand",
      outputBrand: "To Brand",
      selectMarker: "Select a Marker",
      algorithm: "Algorithm",
      markersAvailable: "markers available",
      searchPlaceholder: "Search by name or code...",
      noMarkersFound: "No markers found",
      conversionResults: "Similar Colors",
      converting: "Finding similar colors...",
      noResults: "No similar colors found",
      directMatch: "Direct Match",
      bestMatch: "Best Match",
      viewMore: "View More Matches",
      showLess: "Show Less",
      otherMatches: "Other Similar Matches",
      tips: {
        title: "Tips",
        tip1: "For Ohuhu markers, old and new codes are directly matched when available",
        tip2: "Results are sorted by color similarity using advanced color matching",
        tip3: "The top result is usually the closest match",
        tip4: "Toggle between RGB (faster) and Delta E (perceptually accurate) algorithms to compare results"
      },
      help: {
        title: "How to Use Brand Converter",
        tip1: "Select the brand you want to convert FROM on the left",
        tip2: "Select the brand you want to convert TO on the right",
        tip3: "Search and select a marker to find similar colors in the target brand",
        tip4: "Results show the closest matches with similarity percentages"
      }
    },
    header: {
      subtitle: "Professional marker matching",
      goHomeTitle: "Go to Home Page",
    },
    auth: {
      signIn: "Sign In",
      signInWithGoogle: "Sign in with Google",
      logOut: "Log Out",
      myCollection: "My Collection",
      migrationSuccess: "Your {count} markers are now synced to your Google account!",
      backupKeyWarning: "Your backup key will no longer work. Use Google Sign In to access your collection.",
      syncSuccess: "Your {count} markers are now synced to your Google account!"
    },
    syncStatus: {
      synced: "Synced",
      syncing: "Syncing...",
      offline: "Offline",
      localOnly: "Local only",
      error: "Sync error"
    },
    conflictModal: {
      title: "Collection Conflict",
      description: "Your local and cloud collections differ. Which one do you want to keep?",
      localCollection: "Local Collection",
      cloudCollection: "Google Account Collection",
      markers: "markers",
      lastEdited: "Last edited",
      uploadToCloud: "Upload local markers to cloud",
      downloadToLocal: "Download cloud markers to this device",
      warning: "The other collection will be overwritten and cannot be undone.",
      resolving: "Resolving...",
      unknown: "Unknown"
    },
    googleSyncBanner: {
      title: "Sync your collection",
      description: "Access your markers from any device",
      cta: "Sign in with Google",
      dismiss: "Dismiss"
    },
    common: {
      language: "Language",
      english: "English",
      portuguesePT: "Portuguese (Portugal)",
      needHelp: "Need help? Click here"
    },
    weeklyChallenge: {
      title: "Monthly Challenge",
      currentTheme: "Summer Vibes",
      description: "Create palettes inspired by the vivid colors of summer!",
      hashtag: "#MarkerSyncSummer",
      colors: [
        {
          name: "Pine Canopy",
          hex: "#418878",
          brand: "Ohuhu",
          code: "BG215"
        },
        {
          name: "Evergreen",
          hex: "#5da78d",
          brand: "Ohuhu",
          code: "BG212"
        },
        {
          name: "Cranberry",
          hex: "#9d4c4e",
          brand: "Ohuhu",
          code: "R015"
        },
        {
          name: "Mulled Wine",
          hex: "#86575e",
          brand: "Ohuhu",
          code: "R513"
        },
        {
          name: "Light Gold",
          hex: "#e2c57c",
          brand: "Ohuhu",
          code: "Y26"
        },
        {
          name: "Snow Tint",
          hex: "#f0f4f4",
          brand: "Ohuhu",
          code: "B30"
        }
      ]
    },
    colorPicker: {
        title: "Marker Picker",
        subtitle: "Upload any image and discover the perfect markers to recreate those beautiful colors",
        uploadBtn: "Upload Image",
        colorsLabel: "Colors",
        viewResults: "View Results",
        tapHint: "Tap colors above",
        removeColor: "Remove color",
        selectFromImage: "Select colors from your image",
        clickColorsShort: "Click colors",
        viewPalette: "View Palette",
        finalize: "Finalize Palette",
        share: "Share Palette",
        howTo: {
            title: "How to Find Your Perfect Markers",
            subtitle: "Three simple steps to discover your ideal color palette",
            step1: { title: "Upload Image", desc: "Choose a photo with colors you want to recreate with your markers" },
            step2: { title: "Click Colors", desc: "Click on any color in your image to find the perfect marker match" },
            step3: { title: "Get Your List", desc: "Discover exactly which markers you need to buy for your project" }
        },
        sharePalette: "Share Palette",
        shareModal: {
            title: "My Color Palette",
            shareIntro: "Check out my color palette!",
            copied: "Share link copied to clipboard!",
            copyFallback: "Share this link:",
            fail: "Failed to create share code. Please try again."
        },
        alerts: {
            emptyCollection: "Your collection is empty. Add markers before using \"My Collection\"."
        },
        help: {
            title: "How to Use the Color Picker",
            tip1: "Upload an image with colors you want to match",
            tip2: "Click anywhere on the image to pick a color",
            tip3: "We'll find the closest matching marker from your selected brand",
            tip4: "Build a palette by picking multiple colors, then share or save it"
        }
    },
    imageCanvas: {
        hint: "Click anywhere on the image to pick colors and find matching markers",
        canvas: "Interactive image canvas for color picking",
        zoomIn: "Zoom In",
        zoomOut: "Zoom Out",
        reset: "Reset",
        zoomPercent: "{value}%",
        instructions: "Click colors to select • Use +/- keys to zoom"
    },
    imageScanner: {
        title: "Image Color Scanner (Beta)",
        subtitle: "Upload an image and we'll automatically extract the dominant colors and find the best matching markers from your selected brand.",
        comingSoon: {
            title: "Coming Soon",
            body: "This feature is currently in development. The demo shows how extracted colors are matched to markers, but the analysis process may have limitations. We appreciate your patience as we work to improve this tool!"
        },
        clearAnalysis: "Clear Analysis",
        analyzedImage: "Analyzed Image",
        analyzedImageAlt: "Analyzed reference",
        tryDifferentImage: "Try Different Image",
        matches: {
            title: "Your Perfect Marker Matches",
            subtitle: "Each marker below perfectly matches colors from your image"
        },
        downloadBtn: "Download Analysis Results",
        help: {
            title: "How Image Scanning Works",
            item1: "Upload any image (artwork, photo, inspiration)",
            item2: "We extract the 15 most dominant colors automatically",
            item3: "Each extracted color is matched to the closest marker from your selected brand",
            item4: "Results include similarity scores and color accuracy data",
            item5: "Download the complete analysis as a file for your records"
        },
        brandChangedReanalyzeLog: "Brand changed, would need to re-analyze with new markers",
        alerts: {
            emptyCollection: "Your collection is empty. Add markers before analyzing with \"My Collection\"."
        }
    },
    ohuhuConverter: {
      hero: {
        title: "Ohuhu Code Converter",
        subtitle: "Convert legacy and 2024 Ohuhu marker numbers instantly",
        description: "Perfect when tutorials show only one numbering format. Switch between old and new codes without guessing."
      },
      cards: {
        oldToNew: {
          title: "Old → New",
          label: "Old code",
          placeholder: "e.g., BG3 or R1",
          helper: "Spacing and lowercase are ignored."
        },
        newToOld: {
          title: "New → Old",
          label: "New code",
          placeholder: "e.g., BG215 or R020",
          helper: "Enter any 2024 Ohuhu code."
        }
      },
      result: {
        heading: "Match found",
        nameLabel: "Marker name",
        oldLabel: "Old code",
        newLabel: "New code",
        copyOld: "Copy old code",
        copyNew: "Copy new code",
        copyHex: "Copy hex",
        fillOther: "Send to other converter"
      },
      suggestionsTitle: "Maybe you meant…",
      noMatch: "We couldn't find that code. Double-check the digits and try again.",
      emptyInput: "Start typing a code to see conversions.",
      tips: {
        title: "Helpful reminders",
        bullet1: "Many classic codes map to brand-new hues—always verify the marker name.",
        bullet2: "Use the Ohuhu code toggle while browsing palettes to display both systems everywhere."
      },
      actions: {
        clear: "Clear",
        copied: "Copied!"
      },
      latest: "Synced with the November 2024 Ohuhu numbering refresh."
    },
    legal: {
      backToHome: "Back to Home",
      title: "Terms & Privacy",
      subtitle: "Terms of Service, Privacy Policy, and legal information for MarkerSync",
      lastUpdated: "Last updated: March 2026",

      about: {
        heading: "About MarkerSync",
        body1: "MarkerSync is a free web tool designed to help artists and creatives match colors between different marker brands. All color processing happens directly in your browser for maximum privacy and speed.",
        body2: "This service is provided for personal, non-commercial use. We are an independent tool and are not affiliated with any marker brands mentioned on this website."
      },

      terms: {
        heading: "Terms of Service",
        acceptance: {
          title: "Acceptance of Terms",
          body: "By accessing and using MarkerSync, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use this service."
        },
        license: {
          title: "License to Use",
          body: "We grant you a limited, non-exclusive, non-transferable license to use MarkerSync for personal, non-commercial purposes. You may not use this service for any illegal or unauthorized purpose."
        },
        account: {
          title: "User Accounts (Optional)",
          body: "You may optionally create an account using Google Sign-In to sync your marker collection across devices. By creating an account, you agree to:",
          item1: "Provide accurate information associated with your Google account",
          item2: "Maintain the security of your account credentials",
          item3: "Be responsible for all activity that occurs under your account"
        },
        gallery: {
          title: "Public Gallery",
          body: "The MarkerSync Gallery is a curated, invite-only feature for approved publishers. By publishing palettes to the gallery, you agree to:",
          item1: "Only submit original palettes that you have the right to share",
          item2: "Follow community guidelines — palette names must be appropriate and respectful",
          item3: "Accept that published palettes are visible to all visitors of MarkerSync",
          item4: "Accept that misuse may result in removal from the publisher program",
          username: "Display name: Your palettes will show your username (@handle) if you have set one, otherwise your Google display name. You may also choose to publish fully anonymously, in which case your name will appear as \"MarkerSync User\". You can manage these preferences at any time from your palette settings."
        },
        prohibited: {
          title: "Prohibited Uses",
          item1: "Attempting to interfere with, compromise, or disrupt the service",
          item2: "Using automated systems (bots, scrapers) without permission",
          item3: "Uploading malicious code or content",
          item4: "Violating any applicable laws or regulations"
        },
        termination: {
          title: "Termination",
          body: "We reserve the right to terminate or suspend access to the service at any time, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties."
        }
      },

      privacy: {
        heading: "Privacy Policy",
        dataController: {
          title: "Data Controller",
          body: "MarkerSync (contact: markersync@gmail.com) is the data controller for any personal data processed through this service."
        },
        dataCollection: {
          title: "What Data We Collect",
          body: "MarkerSync is designed with privacy first. We collect minimal data:",
          local: {
            title: "Local Storage",
            body: "Your browser's localStorage stores your preferences and marker collection locally on your device. This data never leaves your device unless you explicitly enable cloud sync."
          },
          images: {
            title: "Uploaded Images",
            body: "Images you upload for color palette generation are processed entirely in your browser. They are never sent to our servers or stored anywhere."
          },
          google: {
            title: "Google Account Data (Optional)",
            body: "If you sign in with Google, we collect and store: email address, display name, profile photo, and a unique user ID. Optionally, you may also set a username (a public handle) which is stored and used as your display name in the gallery. This data is used solely for account identification and profile display."
          },
          backupKeys: {
            title: "Anonymous Cloud Backup (Optional)",
            body: "If you enable cloud backup without a Google account, your collection is stored using a randomly generated backup key. We cannot identify who owns which backup key."
          },
          server: {
            title: "Server Logs",
            body: "Our hosting provider (AWS) may automatically log your IP address for technical and security purposes. We do not access or analyze this data."
          }
        },
        legalBasis: {
          title: "Legal Basis for Processing (GDPR)",
          body: "We process your data based on: (1) your consent when you sign in with Google or enable cloud backup, and (2) legitimate interests in providing and improving the service."
        },
        dataStorage: {
          title: "How Long We Store Data",
          body: "We retain your data as follows:",
          item1: "Google Account Data: Retained until you delete your account or request deletion",
          item2: "Cloud Collections: Retained until you delete them or request deletion"
        },
        thirdParty: {
          title: "Third-Party Service Providers",
          body: "We use trusted third-party services to operate MarkerSync:",
          firebase: "Cloud database services (Google Cloud Platform) for optional account sync and data storage.",
          aws: "Web hosting services for site availability and performance.",
          simpleAnalytics: "Anonymous website analytics (Simple Analytics) for understanding site usage without cookies or personal data collection."
        },
        yourRights: {
          title: "Your Rights (GDPR)",
          body: "Under GDPR, you have the following rights:",
          access: "Right to Access",
          accessBody: "Request a copy of your personal data",
          deletion: "Right to Deletion",
          deletionBody: "Request deletion of your account and all associated data by contacting markersync@gmail.com. We will process requests within 30 days.",
          export: "Right to Data Portability",
          exportBody: "Export your marker collection as a JSON file from the Collection page",
          withdraw: "Right to Withdraw Consent",
          withdrawBody: "Log out and delete your account at any time"
        },
        cookies: {
          title: "Cookies",
          body: "We use Simple Analytics, a privacy-friendly analytics service that does not use cookies or track personal data. We only use essential browser storage (localStorage) to save your preferences locally."
        },
        changes: {
          title: "Changes to Privacy Policy",
          body: "We may update this privacy policy from time to time. Changes will be posted on this page with an updated \"Last updated\" date."
        }
      },

      disclaimer: {
        heading: "Disclaimer",
        service: {
          title: "Service \"As Is\"",
          body: "MarkerSync is provided \"as is\" without warranties of any kind, either express or implied. We do not guarantee uninterrupted access, accuracy, or availability of the service."
        },
        accuracy: {
          title: "Color Accuracy",
          body: "Color matching results are approximations based on visual and technical analysis. We do not guarantee exact color matches between different marker brands. Always test markers before purchasing or use."
        },
        brands: {
          title: "Brand Independence",
          body: "MarkerSync is an independent tool. We are NOT affiliated, sponsored, or endorsed by any marker brands (Ohuhu, Decotime, Touch, Chen Rui, NOTE, Pingo Doce, Lidl, CCHobby, Promarker, or others). All trademarks belong to their respective owners."
        },
        liability: {
          title: "Limitation of Liability",
          body: "By using this website, you agree that we are not liable for any damages, losses, or issues that may result from use of the service, including but not limited to color matching errors, data loss, or service interruptions."
        }
      },

      contact: {
        heading: "Contact Us",
        intro: "For questions about these terms, privacy policy, data deletion requests, or general inquiries, contact us at:"
      }
    },
    paletteSharing: {
      title: "Palette Loading",
      subtitle: "Load your favorite color palettes with simple codes from anywhere!",
      loading: "Loading…",
      load: {
        heading: "Import Palette",
        label: "Enter Palette Code:",
        placeholder: "PC2-...",
        button: "Import",
        success: "Palette loaded successfully!"
      },
      errors: {
        noCode: "Please enter a palette code",
        invalid: "Invalid palette code"
      },
      actions: {
        clear: "Clear",
        copy: "Copy"
      },
      clipboard: { copied: "Copied to clipboard!" },
      preview: {
        heading: "Shared Palette Preview",
        loading: "Loading your shared palette…",
        loadedWithCode: "This palette was shared via code: {code}",
        preparing: "Preparing your colors…"
      },
      demo: {
        code: "Demo Code",
        generated: "Generated Demo Code"
      },
      how: {
        heading: "How Palette Sharing Works",
        create: {
          heading: "Generating Share Codes:",
          item1: "Create palettes using any of our tools",
          item2: 'Click "Share Palette" to get a unique code',
          item3: "Codes are short and easy to share",
          item4: "Works with all palette types and brands"
        },
        use: {
          heading: "Using Share Codes:",
          item1: "Paste any palette code in the input field",
          item2: "Instantly preview the shared palette",
          item3: "Perfect for social media and communities",
          item4: "Share inspiration with fellow artists"
        }
      }
    },

    randomPalette: {
      title: "Create Palettes",
      subtitle: "Use filters to create palettes with specific moods",
      generate: "Create Palette",
      share: "Share Palette",
      filters: "Filters & Options",
      empty: 'No palette created yet. Click "Create Palette" to start!',
      errors: { noPalette: "No palette to share yet. Create one first." },
      generated: {
        title: "Created Palette",
        tip: "Tip: Click the color to keep it during regeneration",
        warning: "Filter constraints may limit available colors"
      },
      nav: {
        prevTitle: "Go back to the previous palette in this category",
        prevDisabled: "No previous palette available",
        nextTitle: "Advance to the next palette in this category",
        nextDisabled: "No next palette available"
      },
      count: {
        label: "colors",
        minusTitle: "Decrease number of colors",
        plusTitle: "Increase number of colors"
      },
      view: {
        compact: "Grid",
        list: "List",
        aria: "Toggle palette layout"
      },
      search: {
        cta: "Search/Replace",
        placeholder: "Type a marker code...",
        aria: "Search markers by code",
        empty: "Start typing to search marker codes or add all markers of a specific brand",
        noResults: "No markers match that code",
        replacePrompt: "Tap a swatch to replace it with your selection",
        cancel: "Cancel replace",
        close: "Close search"
      },
      myCollection: {
        empty: "Empty Collection",
        emptyHint: "Add markers to your collection to generate palettes from them",
        markersInCollection: "markers in your collection",
        manage: "Manage"
      },
      help: {
        title: "How to Use the Palette Creator",
        tip1: "Select a brand or use your personal [collection](/collection) of markers you have at home",
        tip2: "You can create palettes with 3 up to 20 different markers",
        tip3Mobile: "To generate a palette, tap the dice button at the bottom right",
        tip3Desktop: "Click the \"Create Palette\" button to generate a new palette",
        tip4Mobile: "To choose different color moods, tap the palette icon at the bottom",
        tip4Desktop: "Use the Color Moods panel on the left to filter by style",
        tip5: "Click on any color to lock it - locked colors stay when you regenerate",
        tip6: "You can [save](/palettes) the palette to your account or share its code with other people",
        tip7: "Click on the mobile icon to show a compact grid view for screenshots"
      }
    },

    collectionPage: {
      title: "My Collection",
      subtitle: "Build your personal marker collection",
      loading: "Loading your collection...",
      cloudStatus: {
        synced: "Synced",
        localOnly: "Local only"
      },
      search: {
        placeholder: "Search",
        results: "marker(s) found",
        resultsFound: "Found {count} {label}",
        filterLabel: "Marker brands",
        sortLabel: "Order by code",
        sortAsc: "Ascending",
        sortDesc: "Descending",
        applyBrand: "Use current brand",
        filtersTitle: "Marker filters",
        showFilters: "Show",
        hideFilters: "Hide",
        selectionLabel: "Selection",
        selectionEmpty: "Tap markers to select them or choose a brand",
        selectionSummary: "{count} selected",
        addSelected: "Add Selected ({count})",
        addingSelected: "Adding...",
        clearSelection: "Clear selection",
        addAllMarkers: "Add all markers"
      },
      stats: {
        marker: "marker",
        markers: "markers",
        brand: "brand",
        brands: "brands"
      },
      menu: {
        enableCloud: "Enable Cloud Backup",
        loginToSync: "Log In to Sync",
        syncFromCloud: "Sync from Cloud",
        restoreFromCloud: "Restore from cloud key",
        downloadKey: "Download Backup Key",
        copyKey: "Copy Backup Key",
        exportJSON: "Export collection",
        importJSON: "Import from file",
        disconnectCloud: "Disconnect from Cloud",
        clearCollection: "Clear Collection"
      },
      prompts: {
        removeMarkerTitle: "Remove Marker",
        removeMarker: "Remove {name} from your collection?",
        removeBrandTitle: "Remove Brand",
        removeBrand: "Remove all {count} markers from {brand}?",
        clearCollectionTitle: "Clear Collection",
        clearCollection: "Clear all {count} markers from your collection? This cannot be undone.",
        confirmEnableCloud: "Enable cloud backup now? This uploads your collection and generates a private backup key. You'll need this key to restore on other devices.",
        enableCloudTitle: "Enable Cloud Backup?",
        enableCloudConfirm: "Enable Backup",
        cancel: "Cancel",
        cloudLearnMore: {
          prefix: "Learn more about cloud backups",
          link: "here"
        },
        newBackupKey: "Cloud Backup Enabled!\n\nYour backup key: {backupKey}\n\nIMPORTANT: Keep this key PRIVATE!\n• Anyone with this key can VIEW and MODIFY your collection\n• Only use it on devices YOU own\n• Treat it like a password - don't share publicly\n\nClick OK to download the backup key file.",
        restoreBackupKey: "Enter your backup key to restore your collection:\n\n(Format: {format})\n\nIf you don't have a backup key, click Cancel and use \"Import from file\" instead.",
        disconnect: "Disconnect from cloud backup?\n\n• Your collection will remain on this device\n• Cloud data stays intact for other devices\n• You can start a fresh collection on this device\n\nContinue?"
      },
      alerts: {
        cloudEnabled: "Cloud backup enabled! Your collection is now synced.",
        cloudEnableFailed: "Failed to enable cloud backup: {error}",
        restoreSuccess: "Restored {count} markers from cloud!",
        restoreFailed: "Failed to restore from cloud: {error}",
        invalidBackupKey: "Invalid backup key: {error}",
        importSuccess: "Imported {count} markers",
        importFailed: "Failed to import: {error}",
        disconnected: "Disconnected from cloud! You can now start a fresh collection on this device.",
        disconnectFailed: "Failed to disconnect: {error}",
        copyKeySuccess: "Backup key copied: {backupKey}",
        genericError: "{error}"
      },
      emptyState: {
        heading: "Start Building Your Collection",
        description: "Track the markers you own and generate palettes using only YOUR markers. Never get suggestions for markers you don't have!",
        addButton: "Add Your First Marker",
        restoreButton: "Restore Collection"
      },
      whyBuild: {
        title: "Why build a collection?",
        reason1: "Generate palettes only from markers you own",
        reason2: "Sync across all your devices with your account",
        reason3: "Log in to keep your collection safe and tied to your account",
        reason4: "Easy migration from backup key - just sign in"
      },
      buttons: {
        addMarkers: "Add Markers",
        manage: "Manage",
        remove: "Remove",
        cancel: "Cancel",
        clearAll: "Clear All"
      },
      unknownBrand: "Unknown",
      helpNotice: {
        prefix: "Need help with your collection or backup? Contact us at ",
        suffix: " and we'll be happy to help!"
      },
      help: {
        title: "How to Use My Collection",
        tip1: "Add markers you own to build your personal collection",
        tip2: "Use the search to find markers by name, code, or brand",
        tip3: "Alternatively, select a brand and click \"Add all markers\" to add an entire set",
        tip4: "Your collection syncs across devices when signed in with Google"
      }
    },

    gallery: {
      title: "Gallery",
      subtitle: "Discover palettes shared by the MarkerSync community",
      help: {
        title: "About the Gallery",
        tip1: "Browse palettes shared by trusted publishers in the community",
        tip2: "Heart palettes you love — they'll rank higher in Popular",
        tip3: "Click any palette to see its full color list and save it to your account",
        tip4: "The gallery syncs multiple times a day — new and updated palettes appear automatically"
      }
    },

    savedPalettes: {
      page: {
        title: "My Palettes",
        subtitle: "Manage your saved color palettes",
        loading: "Loading palettes...",
        emptyTitle: "No saved palettes yet",
        emptyMessage: "Create a palette and save it to see it here!",
        signInRequired: "Sign In Required",
        signInMessage: "Please sign in to view your saved palettes.",
        paletteCount: "{count} of {max} palettes saved",
        maxReached: "You've reached the maximum of {max} saved palettes. Delete some to save new ones.",
        help: {
          prefix: "Want to publlish your palettes to the gallery? Contact us at ",
          suffix: " and we'll gladly invite you to become a trusted publisher!"
        }
      },
      card: {
        colorCount: "{count} colors",
        color: "color",
        colors: "colors",
        created: "Created"
      },
      saveModal: {
        title: "Save Palette",
        nameLabel: "Palette Name",
        namePlaceholder: "Enter a name for your palette",
        colorCount: "Colors",
        characters: "characters"
      },
      loadModal: {
        title: "Import Palette",
        codeLabel: "Palette Code",
        codePlaceholder: "Paste palette code here (PC2-...)",
        instructions: "Enter a palette code to load it into the creator",
        loadButton: "Import Palette",
        loading: "Loading..."
      },
      detailModal: {
        title: "Palette Details",
        paletteCode: "Palette Code"
      },
      signInPrompt: {
        title: "Sign In to Save Palettes",
        message: "Create an account to save your favorite palettes and access them from any device!",
        benefit1: "Save up to 40 palettes",
        benefit2: "Access from any device",
        benefit3: "Sync with your Google account"
      },
      buttons: {
        save: "Save",
        saving: "Saving...",
        cancel: "Cancel",
        signIn: "Sign In",
        signingIn: "Signing in...",
        view: "View",
        delete: "Delete",
        duplicate: "Duplicate",
        copyCode: "Copy Code",
        copied: "Copied!",
        load: "Import Palette",
        loadToCreate: "Load in Creator",
        editName: "Edit Name",
        createPalette: "Create Palette",
        goHome: "Go Home",
        back: "Back"
      },
      sort: {
        dateNew: "Newest first",
        dateOld: "Oldest first",
        nameAZ: "Name (A-Z)",
        nameZA: "Name (Z-A)"
      },
      alerts: {
        saveSuccess: "Palette saved successfully!",
        nameUpdated: "Palette name updated!",
        deleteSuccess: "Palette deleted successfully!",
        duplicateSuccess: "Palette duplicated successfully!",
        codeCopied: "Palette code copied to clipboard!",
        deleteTitle: "Delete Palette",
        confirmDelete: "Are you sure you want to delete '{name}'?",
        error: "Error",
        saveFailed: "Failed to save palette",
        loadFailed: "Failed to load palette",
        updateFailed: "Failed to update palette",
        deleteFailed: "Failed to delete palette",
        duplicateFailed: "Failed to duplicate palette",
        copyFailed: "Failed to copy code",
        loginFailed: "Failed to sign in"
      },
      errors: {
        nameRequired: "Palette name is required",
        nameTooLong: "Palette name must be 50 characters or less",
        saveFailed: "Failed to save palette",
        codeRequired: "Palette code is required",
        invalidCode: "Invalid palette code format",
        loadFailed: "Failed to load palette. Please check the code and try again."
      },
      help: {
        title: "How to Use My Palettes",
        tip1: "Save palettes from the [Creator](/random) by clicking the heart icon",
        tip2: "Click on a palette to view details, copy the share code or load it back into the Creator",
        tip3: "You can rename, duplicate or delete saved palettes",
        tip4: "Sign in with Google to sync palettes across all your devices"
      }
    },

    suggestions: {
      header: {
        title: "Share Your Ideas",
        subtitle: "Help us improve the Color Palette Generator! Share your suggestions for new features, report bugs, or suggest new marker brands to add"
      },
      form: {
        info: "Your Information",
        name: "Name",
        namePH: "Your name",
        email: "Email",
        emailPH: "your.email@example.com",
        details: "Suggestion Details",
        type: "Type of Suggestion",
        priority: "Priority",
        brand: "Brand Name",
        brandPH: "e.g., Prismacolor, Faber-Castell, etc.",
        subject: "Subject",
        subjectPH: "Brief description of your suggestion",
        message: "Detailed Message",
        messagePH: "Please provide detailed information about your suggestion, including any specific requirements, use cases, or examples..."
      },
      types: {
        feature: "New Feature",
        improvement: "Improvement",
        bug: "Bug Report",
        brand: "New Brand",
        ui: "UI/UX Feedback",
        other: "Other"
      },
      priority: {
        low: "Low Priority",
        medium: "Medium Priority",
        high: "High Priority"
      },
      submit: {
        send: "Send Suggestion",
        sending: "Sending...",
        errorTitle: "Error sending suggestion",
        altContact: "You can contact us directly:"
      },
      thanks: {
        title: "Thank You for Your Suggestion!",
        body: "We've received your feedback and will review it carefully. Your input helps us make the Color Palette Generator even better!"
      },
      errors: {
        nameRequired: "Name is required",
        emailRequired: "Email is required",
        emailInvalid: "Email format is invalid",
        subjectRequired: "Subject is required",
        messageRequired: "Message is required",
        messageMin: "Message should be at least 10 characters",
        unexpected: "Unexpected error. Please try again or contact us directly."
      },
      guidelines: {
        title: "Guidelines",
        item1: "Be kind and respectful",
        item2: "Make suggestions clear and concise",
        item3: "Include examples or use cases when possible",
        item4: "For bugs, include steps to reproduce"
      },
      contact: {
        title: "Other Ways to Reach Us",
        byTeam: ""
      }
    },
    brandSelector: {
      aria: "Brand",
      allBrands: "All Brands",
      myCollection: "My Collection"
    },

    colorMatches: {
      title: "Perfect Color Matches",
      subtitle: "Each color from your image matched with its ideal marker",
      generatePalette: "Create Palette",
      copyList: "Copy Shopping List",
      imageColor: "Image Color",
      markerOfBrand: "{brand} Marker",
      codeLabel: "Code",
      similarity: "Similarity: {value}%",
      copyHex: "Copy {hex}",
      copyHexShort: "Copy Hex",
      copyMarker: "Copy Marker",
      showAlternatives: "Show {count} alternatives",
      hideAlternatives: "Hide alternatives ({count})",
      clickToCopyMarker: "Click to copy marker info",
      na: "N/A",
      list: {
        title: "Marker Shopping List",
        brandHeader: "{brand} Markers ({count}):",
        itemLine: "• {name} ({code}) - {hex}",
        forImageColor: "  → For image color #{index}",
        total: "Total markers needed: {total}",
        GeneratedOn: "Created on {date}"
      }
    },

    colorMatchResult: {
      header: {
        perfectMatch: "Perfect Match Found",
        remove: "Remove this color"
      },
      compare: {
        extracted: "Extracted Color",
        recommended: "Recommended Marker",
        diff: "Color Difference: {from} → {to}"
      },
      alts: {
        explore: "Explore Alternatives",
        backToOriginal: "Back to Original",
        more: "+{more} more similar options available",
        none: "No similar markers found"
      }
    },

    colorSwatch: {
      missing: "Color data missing",
      unknownBrand: "Unknown",
      untitled: "Untitled",
      lock: "Lock color",
      unlock: "Unlock color",
      oldCode: "Old Marker Code",
      newCode: "New Marker Code",
      markerCode: "Marker Code"
    },

    extractedColors: {
      title: "Colors from Your Image",
      subtitle: "Found {count} distinctive colors sorted by importance",
      footer: "These colors will be matched with the best available markers",
      copyTitle: "Copy {hex} • {pct}% importance"
    },

    filters: {
      title: "Color Moods",
      names: {
        Random: "Random",
        Similar: "Similar",
        Warm: "Warm",
        Summer: "Summer",
        Spring: "Spring",
        Autumn: "Autumn",
        Winter: "Winter",
        Light: "Light",
        Dark: "Dark",
        Vivid: "Vivid",
        Muted: "Muted",
        Greenish: "Greenish",
        Yellowish: "Yellowish",
        Reddish: "Reddish",
        Blueish: "Blueish",
        Purplish: "Purplish",
        Neutral: "Neutral",
        Moody: "Moody",
        Pastel: "Pastel",
        Retro: "Retro",
        Vintage: "Vintage",
        Neon: "Neon",
        Day: "Day",
        Night: "Night",
        Dark: "Dark",
        Earth: "Earth",
        Skin: "Skin",
        Cottage: "Cottage"
      }
    },

    finalPalette: {
      title: "Your Color Palette",
      subtitle: "Perfect for your artwork with beautiful colors",
      ready: "Ready to paint and create amazing art",
      oldCode: "Old Code",
      newCode: "New Code",
      markerCode: "Marker Code"
    },

    footer: {
      byTeam: "",
      suggestions: "Suggestions",
      previewWarning: "Color previews are approximations — always test on actual markers",
      legal: "Legal Notice"
    },

    support: {
      title: "Help keep this free",
      subtitle: "If this tool was useful to you, a small contribution helps keeping it online and adding new features.",
      platforms: {
        coffee: "Support with a coffee",
        kofi: "Support on Ko-fi",
        paypal: "Donate via PayPal"
      },
      thankYou: "Every contribution, no matter how small, makes a difference"
    },

    sharePaletteModal: {
      title: "Share This Palette",
      subtitle: "Copy and share this code with friends or on social media",
      paletteCode: "Palette Code",
      copy: "Copy Code",
      copied: "Copied",
      closeAria: "Close",
      imageReference: "Palette Image",
      downloadImage: "Save Palette Image",
      generatingImage: "Generating Image...",
      imageDescription: "Save an image with the palette layout and a QR code.",
      imageError: "Error generating image. Please try again.",
      urlCode: "Shareable URL",
      copyUrl: "Copy URL",
      copiedUrl: "URL Copied",
      noPaletteError: "You need a palette before saving an image.",
      qrHint: "Scan this code to load the palette"
    },

    quality: { perfect: "Perfect", veryGood: "Very Good", good: "Good", fair: "Fair" },
    imageUploader: {
      title: "Upload Your Image ✨",
      subtitle: "We'll analyze your image and recommend the perfect marker palette",
      errorTitle: "Analysis Failed",
      errorHint: "Try a different image with clearer colors.",
      analyzing: "Analyzing colors...",
      tryAnother: "Try another image",
      dropHere: "Drop your image here",
      extracting: "Please wait while we extract the perfect colors",
      uploadDifferent: "Upload a different image to try again",
      orClickToBrowse: "or click to browse files",
      formatsNote: "PNG • JPG • GIF • Max 10MB",
      remove: "Remove",
      reanalyze: "Re-analyze",
      uploadNew: "Upload New",
      fileLabelAnalyzing: "Extracting color palette...",
      fileLabelFailed: "Analysis failed — try a different image",
      fileLabelDone: "Analysis complete!",
      state: {
        dropHere: "Drop your image here",
        orClick: "or click to browse"
      },
      maxSize: "Max 10MB",
      tips: {
        title: "Tips for Better Results",
        tip1: "Use images with clear, distinct colors",
        tip2: "Good lighting produces better color analysis", 
        tip3: "High resolution images work best",
        tip4: "Avoid blurry or low-contrast images"
      },
      alerts: {
        notImage: "Please upload an image file (PNG, JPG, GIF, etc.)",
        tooBig: "Please upload an image smaller than 10MB"
      }
    },
    imageMatchPaletteGrid: {
      cardTitle: "Color Match #{index}",
      fromImage: "From Your Image",
      brandMatch: "{brand} Match",
      similarity: "Similarity",
      colorDifference: "Color Difference",
      copyHexTitle: "Copy hex code",
      perfectFor: "Perfect match for image color #{index}",
      oldCode: "Old Code",
      newCode: "New Code",
      code: "Code",
      copyMarker: "Copy Marker",
      copyHex: "Copy Hex",
      matchBadge: "{quality} Match"
    },
    legalDisclaimer: {
      title: "Disclaimer",
      body1: "This website is an independent tool for color matching of art markers. We are NOT affiliated, sponsored, or endorsed by any of the mentioned brands.",
      point1: "All trademarks belong to their respective owners",
      point2: "Colors are approximations based on visual/technical analysis",
      point3: "We do not guarantee exact color matches between different brands",
      point4: "If you'd like to support this project or say thanks, please contact us",
      list1: [
        "All trademarks belong to their respective owners",
        "Colors are approximations based on visual/technical analysis"
      ],
      list2: [
        "We do not guarantee exact color matches between different brands",
        "If you'd like to support this project or say thanks, please contact us"
      ],
      viewFull: "View complete legal information"
    },

    colorMatching: {
      original: "Original",
      code: "Code",
      bestMatch: "Best Match",
      showAllN: "Show All ({n})",
      showLess: "Show Less",
      copyMarker: "Copy Marker",
      copyHex: "Copy Hex",
      similarity: "Similarity",
      colorDifference: "Color Difference",
      deltaE: "ΔE",
      quality: {
        perfect: "Perfect",
        excellent: "Excellent",
        veryGood: "Very Good",
        good: "Good",
        fair: "Fair"
      },
      brandMatch: "Match {brand}"
    },
  },
  [LANG.PT]: {
    nav: {
      random: "Gerar",
      tools: "Ferramentas",
      picker: "Selecionador",
      scanner: "Leitor",
      load: "Carregar",
      palettes: "Paletas",
      converter: "Conversor",
      gallery: "Galeria",
      collection: "Coleção",
      suggestions: "Sugestões"
    },
    toolsMenu: {
      title: "Escolhe uma Ferramenta",
      subtitle: "Seleciona a ferramenta que queres usar",
      picker: {
        title: "Selecionador de Cores",
        description: "Encontra marcadores selecionando uma cor de uma imagem"
      },
      converter: {
        title: "Conversor de Marcas",
        description: "Encontra cores semelhantes entre diferentes marcas"
      },
      hexFinder: {
        title: "LHexX",
        description: "Encontra o marcador mais próximo para qualquer valor hex"
      }
    },
    hexFinder: {
      title: "Localizador Hex",
      subtitle: "Insere qualquer cor hex e encontra o marcador mais próximo",
      brand: "Marca Alvo",
      hexLabel: "Cor Hex",
      hexPlaceholder: "ex. #A3C4E2 ou A3C4E2",
      invalidHex: "Insere um valor hex de 6 dígitos válido",
      algorithm: "Algoritmo",
      searchButton: "Encontrar Correspondência",
      searching: "A procurar correspondência mais próxima...",
      noResults: "Nenhuma correspondência encontrada",
      yourColor: "A Tua Cor",
      bestMatch: "Melhor Correspondência",
      viewMore: "Ver Mais Correspondências",
      showLess: "Ver Menos",
      otherMatches: "Outras Correspondências Próximas",
      tips: {
        title: "Dicas",
        tip1: "Insere um valor hex com ou sem o prefixo #",
        tip2: "Os resultados são ordenados por semelhança de cor usando correspondência avançada de cores",
        tip3: "O resultado superior é o marcador mais próximo na marca selecionada",
        tip4: "Alterna entre algoritmos RGB (mais rápido) e Delta E (perceptualmente preciso) para comparar resultados"
      },
      help: {
        title: "Como Usar o Localizador Hex",
        tip1: "Seleciona a marca de marcadores que queres pesquisar",
        tip2: "Insere um valor de cor hex (ex. #FF5733 ou FF5733)",
        tip3: "Clica em 'Encontrar Correspondência' para ver os marcadores mais próximos",
        tip4: "Os resultados mostram percentagens de semelhança — quanto maior, mais próximo é o resultado"
      }
    },
    brandConverter: {
      badge: "Conversor de Marcas",
      title: "Conversor de Marcas",
      subtitle: "Encontra cores semelhantes entre diferentes marcas de marcadores",
      inputBrand: "De Marca",
      outputBrand: "Para Marca",
      selectMarker: "Seleciona um Marcador",
      algorithm: "Algoritmo",
      markersAvailable: "marcadores disponíveis",
      searchPlaceholder: "Pesquisar por nome ou código...",
      noMarkersFound: "Nenhum marcador encontrado",
      conversionResults: "Cores Semelhantes",
      converting: "A procurar cores semelhantes...",
      noResults: "Nenhuma cor semelhante encontrada",
      directMatch: "Correspondência Direta",
      bestMatch: "Melhor Correspondência",
      viewMore: "Ver Mais Correspondências",
      showLess: "Ver Menos",
      otherMatches: "Outras Correspondências Semelhantes",
      tips: {
        title: "Dicas",
        tip1: "Para marcadores Ohuhu, códigos antigos e novos são correspondidos diretamente quando disponíveis",
        tip2: "Os resultados são ordenados por semelhança de cor usando correspondência avançada de cores",
        tip3: "O resultado superior é geralmente a correspondência mais próxima",
        tip4: "Alterna entre algoritmos RGB (mais rápido) e Delta E (perceptualmente preciso) para comparar resultados"
      },
      help: {
        title: "Como Usar o Conversor de Marcas",
        tip1: "Seleciona a marca de origem à esquerda",
        tip2: "Seleciona a marca de destino à direita",
        tip3: "Pesquisa e seleciona um marcador para encontrar cores semelhantes na marca de destino",
        tip4: "Os resultados mostram as correspondências mais próximas com percentagens de semelhança"
      }
    },
    header: {
      subtitle: "Correspondência de marcadores",
      goHomeTitle: "Ir para a página inicial",
    },
    auth: {
      signIn: "Entrar",
      signInWithGoogle: "Entrar com Google",
      logOut: "Sair",
      myCollection: "A Minha Coleção",
      migrationSuccess: "Os teus {count} marcadores estão agora sincronizados com a tua conta Google!",
      backupKeyWarning: "A tua chave de backup já não funcionará. Usa o login com Google para aceder à tua coleção.",
      syncSuccess: "Os teus {count} marcadores estão agora sincronizados com a tua conta Google!"
    },
    syncStatus: {
      synced: "Sincronizado",
      syncing: "A sincronizar...",
      offline: "Offline",
      localOnly: "Apenas local",
      error: "Erro de sincronização"
    },
    conflictModal: {
      title: "Conflito de Coleção",
      description: "A tua coleção local e na cloud são diferentes. Qual queres manter?",
      localCollection: "Coleção Local",
      cloudCollection: "Coleção da Conta Google",
      markers: "marcadores",
      lastEdited: "Última edição",
      uploadToCloud: "Enviar marcadores locais para a cloud",
      downloadToLocal: "Descarregar marcadores da cloud para este dispositivo",
      warning: "A outra coleção será substituída e não pode ser desfeito.",
      resolving: "A resolver...",
      unknown: "Desconhecido"
    },
    googleSyncBanner: {
      title: "Sincroniza a tua coleção",
      description: "Acede aos teus marcadores em qualquer dispositivo",
      cta: "Entrar com Google",
      dismiss: "Fechar"
    },
    common: {
      language: "Idioma",
      english: "Inglês",
      portuguesePT: "Português (Portugal)",
      needHelp: "Precisa de ajuda? Clica aqui"
    },
    weeklyChallenge: {
      title: "Desafio Mensal",
      currentTheme: "Inicio da Primavera",
      description: "Cria uma paleta inspirada nas cores da primavera",
      cta: "Obter Paleta de Exemplo",
      hashtag: "#MarkerSyncEarlySpring",
      colors: [
        {
          name: "Verde Pinheiro",
          hex: "#418878",
          brand: "Ohuhu",
          code: "BG215"
        },
        {
          name: "Verde Azevinho",
          hex: "#5da78d",
          brand: "Ohuhu",
          code: "BG212"
        },
        {
          name: "Vermelho Cranberry",
          hex: "#9d4c4e",
          brand: "Ohuhu",
          code: "R015"
        },
        {
          name: "Vinho Quente",
          hex: "#86575e",
          brand: "Ohuhu",
          code: "R513"
        },
        {
          name: "Ouro Suave",
          hex: "#e2c57c",
          brand: "Ohuhu",
          code: "Y26"
        },
        {
          name: "Branco Neve",
          hex: "#f0f4f4",
          brand: "Ohuhu",
          code: "B30"
        }
      ]
    },
    colorPicker: {
        title: "Selecionador de Marcadores",
        subtitle: "Carrega qualquer imagem e descobre os marcadores perfeitos para recriar essas cores",
        uploadBtn: "Carregar Imagem",
        colorsLabel: "Cores",
        viewResults: "Ver Resultados",
        tapHint: "Toca nas cores acima",
        removeColor: "Remover cor",
        selectFromImage: "Seleciona cores a partir da tua imagem",
        clickColorsShort: "Clica nas cores",
        viewPalette: "Ver Paleta",
        finalize: "Finalizar Paleta",
        share: "Partilhar Paleta",
        howTo: {
            title: "Como Encontrar os Marcadores Perfeitos",
            subtitle: "Três passos simples para descobrires a tua paleta ideal",
            step1: { title: "Carregar Imagem", desc: "Escolhe uma foto com as cores que queres recriar com os teus marcadores" },
            step2: { title: "Clicar nas Cores", desc: "Clica em qualquer cor da imagem para encontrar o marcador perfeito" },
            step3: { title: "Obter a Lista", desc: "Descobre exatamente quais os marcadores que precisas de comprar para o teu projeto" }
        },
        sharePalette: "Partilhar Paleta",
        shareModal: {
            title: "A Minha Paleta de Cores",
            shareIntro: "Vê a minha paleta de cores!",
            copied: "Ligação de partilha copiada!",
            copyFallback: "Partilha esta ligação:",
            fail: "Falhou a geração do código de partilha. Tenta novamente."
        },
        alerts: {
            emptyCollection: "A tua coleção está vazia. Adiciona marcadores antes de usares \"A Minha Coleção\"."
        },
        help: {
            title: "Como Usar o Selecionador de Cores",
            tip1: "Carrega uma imagem com as cores que queres corresponder",
            tip2: "Clica em qualquer parte da imagem para escolher uma cor",
            tip3: "Encontramos o marcador mais próximo da marca selecionada",
            tip4: "Constrói uma paleta escolhendo várias cores, depois partilha ou guarda"
        }
    },
    imageScanner: {
        title: "Leitor de Cores por Imagem (Beta)",
        subtitle: "Carrega uma imagem e nós extraímos automaticamente as cores dominantes, encontrando os marcadores mais próximos da marca selecionada.",
        comingSoon: {
            title: "Em Breve",
            body: "Esta funcionalidade está em desenvolvimento. A demo mostra como as cores extraídas são associadas a marcadores, mas o processo de análise pode ter limitações. Agradecemos a tua paciência enquanto melhoramos a ferramenta!"
        },
        clearAnalysis: "Limpar Análise",
        analyzedImage: "Imagem Analisada",
        analyzedImageAlt: "Referência analisada",
        tryDifferentImage: "Experimentar Outra Imagem",
        matches: {
            title: "Correspondências de Marcadores Perfeitas",
            subtitle: "Cada marcador abaixo corresponde às cores da tua imagem"
        },
        downloadBtn: "Descarregar Resultados da Análise",
        help: {
            title: "Como Funciona a Leitura por Imagem",
            item1: "Carrega qualquer imagem (ilustração, foto, inspiração)",
            item2: "Extraímos automaticamente as 12 cores mais dominantes",
            item3: "Cada cor extraída é associada ao marcador mais próximo da marca escolhida",
            item4: "Os resultados incluem pontuações de similaridade e dados de precisão",
            item5: "Descarrega a análise completa num ficheiro para registo"
        },
        brandChangedReanalyzeLog: "Marca alterada — será necessário voltar a analisar com os novos marcadores",
        alerts: {
            emptyCollection: "A tua coleção está vazia. Adiciona marcadores antes de analisares com \"A Minha Coleção\"."
        }
    },
    ohuhuConverter: {
      hero: {
        title: "Conversor de Códigos Ohuhu",
        subtitle: "Converte instantaneamente entre os códigos antigos e os novos da Ohuhu",
        description: "Ideal quando um vídeo mostra apenas um formato. Alterna entre os códigos sem adivinhações."
      },
      cards: {
        oldToNew: {
          title: "Antigo → Novo",
          label: "Código antigo",
          placeholder: "ex.: BG3 ou R1",
          helper: "Ignoramos espaços e maiúsculas/minúsculas."
        },
        newToOld: {
          title: "Novo → Antigo",
          label: "Código novo",
          placeholder: "ex.: BG215 ou R020",
          helper: "Introduz qualquer código Ohuhu 2024."
        }
      },
      result: {
        heading: "Correspondência encontrada",
        nameLabel: "Nome do marcador",
        oldLabel: "Código antigo",
        newLabel: "Código novo",
        copyOld: "Copiar código antigo",
        copyNew: "Copiar código novo",
        copyHex: "Copiar Hex",
        fillOther: "Enviar para o outro conversor"
      },
      suggestionsTitle: "Talvez querias dizer…",
      noMatch: "Não encontrámos esse código. Confirma os dígitos e tenta novamente.",
      emptyInput: "Começa por escrever um código para veres as conversões.",
      tips: {
        title: "Dicas úteis",
        bullet1: "Muitos códigos clássicos correspondem a novas tonalidades—confirma sempre o nome do marcador.",
        bullet2: "Usa o selector de códigos Ohuhu enquanto visualizas paletas para mostrar ambos os sistemas em todo o site."
      },
      actions: {
        clear: "Limpar",
        copied: "Copiado!"
      },
      latest: "Atualizado com a numeração Ohuhu de novembro de 2024."
    },
    legal: {
      backToHome: "Voltar à Página Inicial",
      title: "Termos & Privacidade",
      subtitle: "Termos de Serviço, Política de Privacidade e informação legal do MarkerSync",
      lastUpdated: "Última atualização: Março de 2026",

      about: {
        heading: "Sobre o MarkerSync",
        body1: "O MarkerSync é uma ferramenta web gratuita criada para ajudar artistas e criativos a encontrar correspondências de cores entre diferentes marcas de marcadores. Todo o processamento de cores acontece diretamente no teu navegador para máxima privacidade e velocidade.",
        body2: "Este serviço é fornecido para uso pessoal e não comercial. Somos uma ferramenta independente e não estamos afiliados a nenhuma marca de marcadores mencionada neste website."
      },

      terms: {
        heading: "Termos de Serviço",
        acceptance: {
          title: "Aceitação dos Termos",
          body: "Ao aceder e utilizar o MarkerSync, aceitas estar vinculado a estes Termos de Serviço. Se não concordas com estes termos, por favor não utilizes este serviço."
        },
        license: {
          title: "Licença de Utilização",
          body: "Concedemos-te uma licença limitada, não exclusiva e intransferível para usar o MarkerSync para fins pessoais e não comerciais. Não podes utilizar este serviço para qualquer finalidade ilegal ou não autorizada."
        },
        account: {
          title: "Contas de Utilizador (Opcional)",
          body: "Podes opcionalmente criar uma conta usando o início de sessão do Google para sincronizar a tua coleção de marcadores entre dispositivos. Ao criar uma conta, concordas em:",
          item1: "Fornecer informações precisas associadas à tua conta Google",
          item2: "Manter a segurança das credenciais da tua conta",
          item3: "Ser responsável por toda a atividade que ocorre sob a tua conta"
        },
        gallery: {
          title: "Galeria Pública",
          body: "A Galeria do MarkerSync é uma funcionalidade curada e por convite para editores aprovados. Ao publicar paletas na galeria, concordas em:",
          item1: "Submeter apenas paletas originais que tens o direito de partilhar",
          item2: "Seguir as diretrizes da comunidade — os nomes das paletas devem ser adequados e respeitosos",
          item3: "Aceitar que as paletas publicadas são visíveis a todos os visitantes do MarkerSync",
          item4: "Aceitar que o abuso pode resultar na remoção do programa de editores",
          username: "Nome de apresentação: As tuas paletas mostrarão o teu nome de utilizador (@handle) se tiveres definido um, caso contrário o teu nome Google. Podes também optar por publicar de forma totalmente anónima, sendo que o teu nome aparecerá como \"MarkerSync User\". Podes gerir estas preferências a qualquer momento nas definições das tuas paletas."
        },
        prohibited: {
          title: "Usos Proibidos",
          item1: "Tentar interferir, comprometer ou interromper o serviço",
          item2: "Usar sistemas automatizados (bots, scrapers) sem permissão",
          item3: "Carregar código ou conteúdo malicioso",
          item4: "Violar quaisquer leis ou regulamentos aplicáveis"
        },
        termination: {
          title: "Cessação",
          body: "Reservamo-nos o direito de terminar ou suspender o acesso ao serviço a qualquer momento, sem aviso prévio, por conduta que acreditemos violar estes Termos ou ser prejudicial a outros utilizadores, a nós ou a terceiros."
        }
      },

      privacy: {
        heading: "Política de Privacidade",
        dataController: {
          title: "Responsável pelo Tratamento de Dados",
          body: "MarkerSync (contacto: markersync@gmail.com) é o responsável pelo tratamento de quaisquer dados pessoais processados através deste serviço."
        },
        dataCollection: {
          title: "Que Dados Recolhemos",
          body: "O MarkerSync foi concebido com a privacidade em primeiro lugar. Recolhemos o mínimo de dados:",
          local: {
            title: "Armazenamento Local",
            body: "O localStorage do teu navegador armazena as tuas preferências e coleção de marcadores localmente no teu dispositivo. Estes dados nunca saem do teu dispositivo, a menos que atives explicitamente a sincronização na cloud."
          },
          images: {
            title: "Imagens Carregadas",
            body: "As imagens que carregas para geração de paletas de cores são processadas inteiramente no teu navegador. Nunca são enviadas para os nossos servidores nem armazenadas."
          },
          google: {
            title: "Dados da Conta Google (Opcional)",
            body: "Se iniciares sessão com o Google, recolhemos e armazenamos: endereço de email, nome de apresentação, foto de perfil e um ID de utilizador único. Opcionalmente, podes também definir um nome de utilizador (um handle público) que é armazenado e utilizado como nome de apresentação na galeria. Estes dados são usados exclusivamente para identificação da conta e apresentação do perfil."
          },
          backupKeys: {
            title: "Backup Anónimo na Cloud (Opcional)",
            body: "Se ativares o backup na cloud sem uma conta Google, a tua coleção é armazenada usando uma chave de backup gerada aleatoriamente. Não podemos identificar quem possui cada chave de backup."
          },
          server: {
            title: "Registos do Servidor",
            body: "O nosso fornecedor de alojamento (AWS) pode registar automaticamente o teu endereço IP para fins técnicos e de segurança. Não acedemos nem analisamos estes dados."
          }
        },
        legalBasis: {
          title: "Base Legal para Processamento (RGPD)",
          body: "Processamos os teus dados com base em: (1) o teu consentimento quando inicias sessão com o Google ou ativas o backup na cloud, e (2) interesses legítimos em fornecer e melhorar o serviço."
        },
        dataStorage: {
          title: "Quanto Tempo Armazenamos os Dados",
          body: "Retemos os teus dados da seguinte forma:",
          item1: "Dados da Conta Google: Retidos até eliminares a tua conta ou solicitares a eliminação",
          item2: "Coleções na Cloud: Retidas até as eliminares ou solicitares a eliminação"
        },
        thirdParty: {
          title: "Prestadores de Serviços Terceiros",
          body: "Utilizamos serviços de terceiros confiáveis para operar o MarkerSync:",
          firebase: "Serviços de base de dados na cloud (Google Cloud Platform) para sincronização opcional de conta e armazenamento de dados.",
          aws: "Serviços de alojamento web para disponibilidade e desempenho do site.",
          simpleAnalytics: "Analytics anónimos do site (Simple Analytics) para compreender o uso do site sem cookies ou recolha de dados pessoais."
        },
        yourRights: {
          title: "Os Teus Direitos (RGPD)",
          body: "Ao abrigo do RGPD, tens os seguintes direitos:",
          access: "Direito de Acesso",
          accessBody: "Solicitar uma cópia dos teus dados pessoais",
          deletion: "Direito ao Apagamento",
          deletionBody: "Solicitar a eliminação da tua conta e todos os dados associados contactando markersync@gmail.com. Processaremos os pedidos num prazo de 30 dias.",
          export: "Direito à Portabilidade dos Dados",
          exportBody: "Exportar a tua coleção de marcadores como ficheiro JSON a partir da página Coleção",
          withdraw: "Direito de Retirar o Consentimento",
          withdrawBody: "Terminar sessão e eliminar a tua conta a qualquer momento"
        },
        cookies: {
          title: "Cookies",
          body: "Usamos Simple Analytics, um serviço de analytics que respeita a privacidade e não usa cookies nem rastreia dados pessoais. Apenas usamos armazenamento essencial do navegador (localStorage) para guardar as tuas preferências localmente."
        },
        changes: {
          title: "Alterações à Política de Privacidade",
          body: "Podemos atualizar esta política de privacidade periodicamente. As alterações serão publicadas nesta página com uma data de \"Última atualização\" atualizada."
        }
      },

      disclaimer: {
        heading: "Declaração de Responsabilidade",
        service: {
          title: "Serviço \"Tal Como Está\"",
          body: "O MarkerSync é fornecido \"tal como está\" sem garantias de qualquer tipo, expressas ou implícitas. Não garantimos acesso ininterrupto, precisão ou disponibilidade do serviço."
        },
        accuracy: {
          title: "Precisão das Cores",
          body: "Os resultados de correspondência de cores são aproximações baseadas em análise visual e técnica. Não garantimos correspondências exatas de cores entre diferentes marcas de marcadores. Testa sempre os marcadores antes de comprar ou usar."
        },
        brands: {
          title: "Independência de Marcas",
          body: "O MarkerSync é uma ferramenta independente. NÃO estamos afiliados, patrocinados ou aprovados por nenhuma marca de marcadores (Ohuhu, Decotime, Touch, Chen Rui, NOTE, Pingo Doce, Lidl, CCHobby, Promarker, ou outras). Todas as marcas pertencem aos respetivos proprietários."
        },
        liability: {
          title: "Limitação de Responsabilidade",
          body: "Ao utilizar este website, concordas que não somos responsáveis por quaisquer danos, perdas ou problemas que possam resultar do uso do serviço, incluindo mas não limitado a erros de correspondência de cores, perda de dados ou interrupções do serviço."
        }
      },

      contact: {
        heading: "Contacta-nos",
        intro: "Para questões sobre estes termos, política de privacidade, pedidos de eliminação de dados ou questões gerais, contacta-nos em:"
      }
    },
    paletteSharing: {
      title: "Carregamento de Paletas",
      subtitle: "Carrega as tuas paletas favoritas com códigos simples, a partir de qualquer lugar!",
      loading: "A carregar…",
      load: {
        heading: "Carregar Paleta",
        label: "Introduz o Código da Paleta:",
        placeholder: "PC2-...",
        button: "Carregar",
        success: "Paleta carregada com sucesso!"
      },
      errors: {
        noCode: "Por favor, introduz um código de paleta",
        invalid: "Código de paleta inválido"
      },
      actions: {
        clear: "Limpar",
        copy: "Copiar"
      },
      clipboard: { copied: "Copiado para a área de transferência!" },
      preview: {
        heading: "Pré-visualização da Paleta Partilhada",
        loading: "A carregar a tua paleta partilhada…",
        loadedWithCode: "Esta paleta foi partilhada com o código: {code}",
        preparing: "A preparar as tuas cores…"
      },
      demo: {
        code: "Código de Demonstração",
        generated: "Gerar Código de Demonstração"
      },
      how: {
        heading: "Como funciona a partilha de paletas",
        create: {
          heading: "Criar códigos para partilhar:",
          item1: "Gera paletas com qualquer uma das nossas ferramentas",
          item2: 'Clica em "Partilhar Paleta" para obteres um código',
          item3: "Os códigos são curtos e fáceis de partilhar",
          item4: "Funciona com todos os tipos de paletas e marcas"
        },
        use: {
          heading: "Usar Códigos de Partilha:",
          item1: "Cola qualquer código de paleta no campo de input",
          item2: "Vê de imediato a pré-visualização da paleta",
          item3: "Perfeito para redes sociais e comunidades",
          item4: "Partilha inspiração com outros artistas"
        }
      }
    },

    randomPalette: {
      title: "Gerar Paletas",
      subtitle: "Usa filtros para gerar paletas com estados de espírito até 18 cores diferentes",
      generate: "Gerar Paleta",
      share: "Partilhar Paleta",
      filters: "Filtros & Opções",
      empty: 'Ainda não existe uma paleta. Clica em "Gerar Paleta" para começar!',
      errors: { noPalette: "Ainda não há paleta para partilhar. Gera uma primeiro." },
      generated: {
        title: "Paleta Gerada",
        tip: "Dica: Clica na cor para manter durante novas gerações",
        warning: "As restrições dos filtros podem limitar as cores disponíveis"
      },
      nav: {
        prevTitle: "Voltar à paleta anterior desta categoria",
        prevDisabled: "Nenhuma paleta anterior disponível",
        nextTitle: "Avançar para a próxima paleta desta categoria",
        nextDisabled: "Nenhuma paleta seguinte disponível"
      },
      count: {
        label: "cores",
        minusTitle: "Diminuir número de cores",
        plusTitle: "Aumentar número de cores"
      },
      view: {
        compact: "Grelha",
        list: "Lista",
        aria: "Alternar layout da paleta"
      },
      search: {
        cta: "Procurar/Substituir",
        placeholder: "Escreve o código do marcador...",
        aria: "Procurar marcadores por código",
        empty: "Começa a escrever para procurar códigos ou adiciona todos os marcadores de uma marca",
        noResults: "Nenhum marcador corresponde a esse código",
        replacePrompt: "Toca numa cor para a substituir pela seleção",
        cancel: "Cancelar substituição",
        close: "Fechar pesquisa"
      },
      myCollection: {
        empty: "Coleção Vazia",
        emptyHint: "Adiciona marcadores à tua coleção para gerar paletas a partir deles",
        markersInCollection: "marcadores na tua coleção",
        manage: "Gerir"
      },
      help: {
        title: "Como Usar o Criador de Paletas",
        tip1: "Seleciona uma marca ou usa a tua [coleção](/collection) pessoal de marcadores que tens em casa",
        tip2: "Podes criar paletas com 3 até 20 marcadores diferentes",
        tip3Mobile: "Para gerar uma paleta, toca no botão dos dados no canto inferior direito",
        tip3Desktop: "Clica no botão \"Gerar Paleta\" para criar uma nova paleta",
        tip4Mobile: "Para escolher diferentes tons de cor, toca no ícone da paleta em baixo",
        tip4Desktop: "Usa o painel de Tons de Cor à esquerda para filtrar por estilo",
        tip5: "Clica em qualquer cor para a bloquear - cores bloqueadas mantêm-se quando regeneras",
        tip6: "Podes [guardar](/palettes) a paleta na tua conta ou partilhar o código com outras pessoas",
        tip7: "Clica no ícon do smartphone para veres a paleta compacta para capturas de ecrã"
      }
    },

    collectionPage: {
      title: "A Minha Coleção",
      subtitle: "Constrói a tua coleção pessoal de marcadores",
      loading: "A carregar a tua coleção...",
      cloudStatus: {
        synced: "Sincronizado",
        localOnly: "Apenas local"
      },
      search: {
        placeholder: "Pesquisar",
        results: "marcador(es) encontrado(s)",
        resultsFound: "Encontrados {count} {label}",
        filterLabel: "Marcas de marcadores",
        sortLabel: "Ordenar por código",
        sortAsc: "Ascendente",
        sortDesc: "Descendente",
        applyBrand: "Usar marca atual",
        filtersTitle: "Filtros de marcadores",
        showFilters: "Mostrar",
        hideFilters: "Esconder",
        selectionLabel: "Seleção",
        selectionEmpty: "Toca nos marcadores para os selecionares ou escolhe uma marca inteira",
        selectionSummary: "{count} selecionados",
        addSelected: "Adicionar Selecionados ({count})",
        addingSelected: "A adicionar...",
        clearSelection: "Limpar seleção",
        addAllMarkers: "Adicionar todos os marcadores"
      },
      stats: {
        marker: "marcador",
        markers: "marcadores",
        brand: "marca",
        brands: "marcas"
      },
      menu: {
        enableCloud: "Ativar Backup na Cloud",
        loginToSync: "Entrar para Sincronizar",
        syncFromCloud: "Sincronizar da Cloud",
        restoreFromCloud: "Restaurar da chave da cloud",
        downloadKey: "Descarregar Chave de Backup",
        copyKey: "Copiar Chave de Backup",
        exportJSON: "Exportar coleção",
        importJSON: "Importar ficheiro",
        disconnectCloud: "Desconectar da Cloud",
        clearCollection: "Limpar Coleção"
      },
      prompts: {
        removeMarkerTitle: "Remover Marcador",
        removeMarker: "Remover {name} da tua coleção?",
        removeBrandTitle: "Remover Marca",
        removeBrand: "Remover todos os {count} marcadores de {brand}?",
        clearCollectionTitle: "Limpar Coleção",
        clearCollection: "Limpar todos os {count} marcadores da tua coleção? Esta ação é irreversível.",
        confirmEnableCloud: "Ativar backup na cloud? Isto envia a tua coleção e gera uma chave de backup privada. Vais precisar desta chave para restaurar noutros dispositivos.",
        enableCloudTitle: "Ativar backup na cloud?",
        enableCloudConfirm: "Ativar backup",
        cancel: "Cancelar",
        cloudLearnMore: {
          prefix: "Sabe mais sobre os backups na cloud",
          link: "aqui"
        },
        newBackupKey: "Backup na Cloud Ativado!\n\nA tua chave de backup: {backupKey}\n\nIMPORTANTE: Mantém esta chave PRIVADA!\n• Qualquer pessoa com esta chave pode VER e MODIFICAR a tua coleção\n• Usa-a apenas em dispositivos que são teus\n• Trata-a como uma palavra-passe - não partilhes publicamente\n\nClica OK para descarregar o ficheiro da chave.",
        restoreBackupKey: "Introduz a tua chave de backup para restaurar a coleção:\n\n(Formato: {format})\n\nSe não tens chave, clica em Cancelar e usa \"Importar ficheiro\".",
        disconnect: "Desconectar do backup na cloud?\n\n• A tua coleção fica neste dispositivo\n• Os dados na cloud mantêm-se para outros dispositivos\n• Podes começar uma coleção nova neste dispositivo\n\nContinuar?"
      },
      alerts: {
        cloudEnabled: "Backup na cloud ativado! A tua coleção está sincronizada.",
        cloudEnableFailed: "Falha ao ativar backup na cloud: {error}",
        restoreSuccess: "Restaurados {count} marcadores da cloud!",
        restoreFailed: "Falha ao restaurar da cloud: {error}",
        invalidBackupKey: "Chave de backup inválida: {error}",
        importSuccess: "Importados {count} marcadores",
        importFailed: "Falha ao importar: {error}",
        disconnected: "Desconectado da cloud! Podes começar uma coleção nova neste dispositivo.",
        disconnectFailed: "Falha ao desconectar: {error}",
        copyKeySuccess: "Chave de backup copiada: {backupKey}",
        genericError: "Erro: {error}"
      },
      emptyState: {
        heading: "Começa a Construir a Tua Coleção",
        description: "Regista os marcadores que tens e gera paletas usando apenas OS TEUS marcadores. Nunca mais recebes sugestões de marcadores que não tens!",
        addButton: "Adiciona o Teu Primeiro Marcador",
        restoreButton: "Restaurar Coleção"
      },
      whyBuild: {
        title: "Porquê construir uma coleção?",
        reason1: "Gera paletas apenas com os marcadores que tens",
        reason2: "Sincroniza em todos os teus dispositivos com a tua conta",
        reason3: "Faz login para nunca perder a tua coleção",
        reason4: "Migração fácil de backup key - basta iniciar sessão"
      },
      buttons: {
        addMarkers: "Adicionar Marcadores",
        manage: "Gerir",
        remove: "Remover",
        cancel: "Cancelar",
        clearAll: "Limpar Tudo"
      },
      unknownBrand: "Marca desconhecida",
      helpNotice: {
        prefix: "Precisas de ajuda com a tua coleção ou backup? Contacta-nos em ",
        suffix: " e teremos todo o gosto em ajudar!"
      },
      help: {
        title: "Como Usar A Minha Coleção",
        tip1: "Adiciona marcadores que tens para construir a tua coleção pessoal",
        tip2: "Usa a pesquisa para encontrar marcadores por nome, código ou marca",
        tip3: "Alternativamente, seleciona uma marca e clica \"Adicionar todos\" para adicionar um conjunto inteiro",
        tip4: "A tua coleção sincroniza entre dispositivos quando iniciares sessão com Google"
      }
    },

    gallery: {
      title: "Galeria",
      subtitle: "Descobre paletas partilhadas pela comunidade MarkerSync",
      help: {
        title: "Sobre a Galeria",
        tip1: "Explora paletas partilhadas por editores de confiança da comunidade",
        tip2: "Dá coração às paletas que gostas — sobem no ranking Popular",
        tip3: "Clica em qualquer paleta para ver as cores e guardar na tua conta",
        tip4: "A galeria sincroniza várias vezes por dia — novas paletas aparecem automaticamente"
      }
    },

    savedPalettes: {
      page: {
        title: "As Minhas Paletas",
        subtitle: "Gere as tuas paletas de cores guardadas",
        loading: "A carregar paletas...",
        emptyTitle: "Ainda não tens paletas guardadas",
        emptyMessage: "Cria uma paleta e guarda-a para a veres aqui!",
        signInRequired: "Início de Sessão Necessário",
        signInMessage: "Por favor, inicia sessão para ver as tuas paletas guardadas.",
        paletteCount: "{count} de {max} paletas guardadas",
        maxReached: "Alcançaste o número máximo de paletas guardadas.",
        help: {
          prefix: "Precisas de ajuda com as tuas paletas guardadas? Contacta-nos em ",
          suffix: " e teremos todo o gosto em ajudar!"
        }
      },
      card: {
        colorCount: "{count} cores",
        color: "cor",
        colors: "cores",
        created: "Criada"
      },
      saveModal: {
        title: "Guardar Paleta",
        nameLabel: "Nome da Paleta",
        namePlaceholder: "Introduz um nome para a tua paleta",
        colorCount: "Cores",
        characters: "caracteres"
      },
      loadModal: {
        title: "Carregar Paleta",
        codeLabel: "Código da Paleta",
        codePlaceholder: "Cola o código da paleta aqui (PC2-...)",
        instructions: "Introduz um código de paleta para a carregar no criador",
        loadButton: "Carregar Paleta",
        loading: "A carregar..."
      },
      detailModal: {
        title: "Detalhes da Paleta",
        paletteCode: "Código da Paleta"
      },
      signInPrompt: {
        title: "Inicia Sessão para Guardar Paletas",
        message: "Cria uma conta para guardar as tuas paletas favoritas e acedê-las de qualquer dispositivo!",
        benefit1: "Guarda até 25 paletas",
        benefit2: "Acede de qualquer dispositivo",
        benefit3: "Sincroniza com a tua conta Google"
      },
      buttons: {
        save: "Guardar",
        saving: "A guardar...",
        cancel: "Cancelar",
        signIn: "Entrar com Google",
        signingIn: "A entrar...",
        view: "Ver",
        delete: "Eliminar",
        duplicate: "Duplicar",
        copyCode: "Copiar Código",
        copied: "Copiado!",
        load: "Carregar Paleta",
        loadToCreate: "Carregar no Criador",
        editName: "Editar Nome",
        createPalette: "Criar Paleta",
        goHome: "Ir para Início",
        back: "Voltar"
      },
      sort: {
        dateNew: "Mais recentes",
        dateOld: "Mais antigas",
        nameAZ: "Nome (A-Z)",
        nameZA: "Nome (Z-A)"
      },
      alerts: {
        saveSuccess: "Paleta guardada com sucesso!",
        nameUpdated: "Nome da paleta atualizado!",
        deleteSuccess: "Paleta eliminada com sucesso!",
        duplicateSuccess: "Paleta duplicada com sucesso!",
        codeCopied: "Código da paleta copiado!",
        deleteTitle: "Eliminar Paleta",
        confirmDelete: "Tens a certeza que queres eliminar '{name}'?",
        error: "Erro",
        saveFailed: "Falha ao guardar paleta",
        loadFailed: "Falha ao carregar paleta",
        updateFailed: "Falha ao atualizar paleta",
        deleteFailed: "Falha ao eliminar paleta",
        duplicateFailed: "Falha ao duplicar paleta",
        copyFailed: "Falha ao copiar código",
        loginFailed: "Falha ao iniciar sessão"
      },
      errors: {
        nameRequired: "O nome da paleta é obrigatório",
        nameTooLong: "O nome da paleta deve ter 50 caracteres ou menos",
        saveFailed: "Falha ao guardar paleta",
        codeRequired: "O código da paleta é obrigatório",
        invalidCode: "Formato de código de paleta inválido",
        loadFailed: "Falha ao carregar paleta. Verifica o código e tenta novamente."
      },
      help: {
        title: "Como Usar As Minhas Paletas",
        tip1: "Guarda paletas do [Criador](/random) clicando no ícone do coração",
        tip2: "Clica numa paleta para ver detalhes, copiar o código de partilha ou carregá-la no Criador",
        tip3: "Podes editar o nome, duplicar ou apagar paletas guardadas",
        tip4: "Inicia sessão com Google para sincronizar paletas em todos os teus dispositivos"
      }
    },

    suggestions: {
      header: {
        title: "Partilha as Tuas Ideias",
        subtitle: "Ajuda-nos a melhorar o Gerador de Paletas! Partilha sugestões de novas funcionalidades, reporta bugs ou propõe novas marcas de marcadores"
      },
      form: {
        info: "Os Teus Dados",
        name: "Nome",
        namePH: "O teu nome",
        email: "Email",
        emailPH: "o.teu.email@exemplo.com",
        details: "Detalhes da Sugestão",
        type: "Tipo de Sugestão",
        priority: "Prioridade",
        brand: "Nome da Marca",
        brandPH: "ex.: Prismacolor, Faber-Castell, etc.",
        subject: "Assunto",
        subjectPH: "Descrição breve da tua sugestão",
        message: "Mensagem Detalhada",
        messagePH: "Fornece pormenores sobre a tua sugestão, incluindo requisitos, casos de uso ou exemplos..."
      },
      types: {
        feature: "Nova Funcionalidade",
        improvement: "Melhoria",
        bug: "Relato de Bug",
        brand: "Nova Marca",
        ui: "Feedback de UI/UX",
        other: "Outro"
      },
      priority: {
        low: "Baixa Prioridade",
        medium: "Média Prioridade",
        high: "Alta Prioridade"
      },
      submit: {
        send: "Enviar Sugestão",
        sending: "A enviar...",
        errorTitle: "Erro ao enviar a sugestão",
        altContact: "Podes contactar-nos diretamente:"
      },
      thanks: {
        title: "Obrigado pela Tua Sugestão!",
        body: "Recebemos o teu feedback e iremos analisá-lo com atenção. A tua participação ajuda-nos a tornar o Gerador de Paletas ainda melhor!"
      },
      errors: {
        nameRequired: "O nome é obrigatório",
        emailRequired: "O email é obrigatório",
        emailInvalid: "Formato de email inválido",
        subjectRequired: "O assunto é obrigatório",
        messageRequired: "A mensagem é obrigatória",
        messageMin: "A mensagem deve ter pelo menos 10 caracteres",
        unexpected: "Erro inesperado. Tenta novamente ou contacta-nos diretamente."
      },
      guidelines: {
        title: "Boas Práticas",
        item1: "Sê simpático e respeitador",
        item2: "Torna as sugestões claras e concisas",
        item3: "Inclui exemplos ou casos de uso quando possível",
        item4: "Para bugs, descreve os passos para reproduzir"
      },
      contact: {
        title: "Outras Formas de Contacto",
        byTeam: ""
      }
    },
    brandSelector: {
      aria: "Marca",
      allBrands: "Todas as Marcas",
      myCollection: "A Minha Coleção"
    },

    colorMatches: {
      title: "Correspondências de Cores Perfeitas",
      subtitle: "Cada cor da tua imagem corresponde ao marcador ideal",
      generatePalette: "Criar Paleta",
      copyList: "Copiar Lista de Compras",
      imageColor: "Cor da Imagem",
      markerOfBrand: "Marcador {brand}",
      codeLabel: "Código",
      similarity: "Similaridade: {value}%",
      copyHex: "Copiar {hex}",
      copyHexShort: "Copiar Hex",
      copyMarker: "Copiar Marcador",
      showAlternatives: "Mostrar {count} alternativas",
      hideAlternatives: "Esconder alternativas ({count})",
      clickToCopyMarker: "Clica para copiar a info do marcador",
      na: "—",
      list: {
        title: "Lista de Compras de Marcadores",
        brandHeader: "Marcadores {brand} ({count}):",
        itemLine: "• {name} ({code}) - {hex}",
        forImageColor: "  → Para a cor da imagem #{index}",
        total: "Total de marcadores necessários: {total}",
        GeneratedOn: "Gerado em {date}"
      }
    },

    colorMatchResult: {
      header: {
        perfectMatch: "Correspondência Perfeita Encontrada",
        remove: "Remover esta cor"
      },
      compare: {
        extracted: "Cor Extraída",
        recommended: "Marcador Recomendado",
        diff: "Diferença de Cor: {from} → {to}"
      },
      alts: {
        explore: "Explorar Alternativas",
        backToOriginal: "Voltar ao Original",
        more: "+{more} opções semelhantes disponíveis",
        none: "Não foram encontrados marcadores semelhantes"
      }
    },

    colorSwatch: {
      missing: "Dados da cor em falta",
      unknownBrand: "Desconhecida",
      untitled: "Sem título",
      lock: "Bloquear cor",
      unlock: "Desbloquear cor",
      oldCode: "Código Antigo",
      newCode: "Código Novo",
      markerCode: "Código do Marcador"
    },

    extractedColors: {
      title: "Cores da Tua Imagem",
      subtitle: "Foram encontradas {count} cores distintas ordenadas por importância",
      footer: "Estas cores serão associadas aos melhores marcadores disponíveis",
      copyTitle: "Copiar {hex} • {pct}% de importância"
    },

    filters: {
      title: "Tons de Cor",
      names: {
        Random: "Aleatória",
        Similar: "Semelhante",
        Warm: "Quente",
        Summer: "Verão",
        Spring: "Primavera",
        Autumn: "Outono",
        Winter: "Inverno",
        Light: "Clara",
        Dark: "Escura",
        Vivid: "Viva",
        Muted: "Suave",
        Greenish: "Esverdeada",
        Yellowish: "Amarelada",
        Reddish: "Avermelhada",
        Blueish: "Azulada",
        Purplish: "Arroxeada",
        Neutral: "Neutra",
        Moody: "Sombria",
        Pastel: "Pastel",
        Retro: "Retro",
        Vintage: "Vintage",
        Neon: "Néon",
        Day: "Dia",
        Night: "Noite",
        Dark: "Escuro",
        Earth: "Terra",
        Skin: "Pele",
        Cottage: "Campestre"
      }
    },

    finalPalette: {
      title: "A Tua Paleta de Cores",
      subtitle: "Perfeita para o teu trabalho com cores lindas",
      ready: "Pronta para pintar e criar arte incrível",
      oldCode: "Código Antigo",
      newCode: "Código Novo",
      markerCode: "Código do Marcador"
    },

    footer: {
      byTeam: "",
      suggestions: "Sugestões",
      previewWarning: "As pré-visualizações de cor são aproximações — testa sempre nos marcadores reais",
      legal: "Aviso Legal"
    },

    support: {
      title: "Ajuda a manter isto gratuito",
      subtitle: "Se esta ferramenta te foi útil, uma pequena contribuição ajuda a mantê-la online e a adicionar novas funcionalidades.",
      platforms: {
        coffee: "Apoiar com um café",
        kofi: "Apoiar no Ko-fi", 
        paypal: "Doar via PayPal"
      },
      thankYou: "Cada contribuição, por mais pequena, faz a diferença"
    },
    legalDisclaimer: {
      title: "Aviso",
      body1: "Este site é uma ferramenta independente para correspondência de cores de marcadores artísticos. NÃO estamos afiliados, patrocinados ou endossados por nenhuma das marcas mencionadas.",
      point1: "Todas as marcas registadas pertencem aos respetivos proprietários",
      point2: "As cores são aproximações baseadas em análise visual/técnica",
      point3: "Não garantimos correspondências exatas entre marcas diferentes",
      point4: "Se quiseres apoiar este projeto ou dizer obrigado, contacta-nos",
      list1: [
        "Todas as marcas registadas pertencem aos respetivos proprietários",
        "As cores são aproximações baseadas em análise visual/técnica"
      ],
      list2: [
        "Não garantimos correspondências exatas entre marcas diferentes",
        "Se quiseres apoiar este projeto ou dizer obrigado, contacta-nos"
      ],
      viewFull: "Ver informação legal completa"
    },
    imageUploader: {
      title: "Carrega a tua imagem",
      subtitle: "Vamos analisar a imagem e recomendar a paleta perfeita de marcadores",
      errorTitle: "Falha na Análise",
      errorHint: "Tenta outra imagem com cores mais nítidas.",
      analyzing: "A analisar as cores...",
      tryAnother: "Tenta outra imagem",
      dropHere: "Larga a tua imagem aqui",
      extracting: "A extrair as melhores cores, aguarda por favor",
      uploadDifferent: "Carrega uma imagem diferente para tentar novamente",
      orClickToBrowse: "ou clica para escolher ficheiros",
      formatsNote: "PNG • JPG • GIF • Máx. 10MB",
      remove: "Remover",
      reanalyze: "Reanalisar",
      uploadNew: "Carregar Nova",
      fileLabelAnalyzing: "A extrair a paleta de cores...",
      fileLabelFailed: "Análise falhou — tenta outra imagem",
      fileLabelDone: "Análise concluída!",
      state: {
        dropHere: "Larga a tua imagem aqui",
        orClick: "ou clica para escolher"
      },
      maxSize: "Máx. 10MB",
      tips: {
        title: "Dicas para Melhores Resultados",
        tip1: "Usa imagens com cores nítidas e distintas",
        tip2: "Boa iluminação produz melhor análise de cores",
        tip3: "Imagens de alta resolução funcionam melhor",
        tip4: "Evita imagens desfocadas ou com pouco contraste"
      },
      alerts: {
        notImage: "Por favor, envia um ficheiro de imagem (PNG, JPG, GIF, etc.)",
        tooBig: "Envia uma imagem com menos de 10MB"
      }
    },
    quality: { perfect: "Perfeito", veryGood: "Muito Bom", good: "Bom", fair: "Razoável" },
    imageMatchPaletteGrid: {
      cardTitle: "Correspondência de Cor #{index}",
      fromImage: "Da tua imagem",
      brandMatch: "Match {brand}",
      similarity: "Similaridade",
      colorDifference: "Diferença de Cor",
      copyHexTitle: "Copiar código hex",
      perfectFor: "Combina na perfeição com a cor #{index} da imagem",
      oldCode: "Código Antigo",
      newCode: "Código Novo",
      code: "Código",
      copyMarker: "Copiar Marcador",
      copyHex: "Copiar Hex",
      matchBadge: "Match {quality}"
    },
    imageCanvas: {
      zoomIn: "Aproximar",
      zoomOut: "Afastar",
      reset: "Repor",
      zoomPercent: "{value}%",
      instructions: "Clica nas cores para selecionar • Usa as teclas +/- para ampliar",
      hint: "Clica em qualquer lugar da imagem para selecionar cores e encontrar marcadores correspondentes",
      canvas: "Canvas interativo para seleção de cores"
    },
    colorMatching: {
      original: "Original",
      code: "Código",
      bestMatch: "Melhor Correspondência",
      showAllN: "Mostrar Tudo ({n})",
      showLess: "Mostrar Menos",
      copyMarker: "Copiar Marcador",
      copyHex: "Copiar Hex",
      similarity: "Similaridade",
      colorDifference: "Diferença de Cor",
      deltaE: "ΔE",
      quality: {
        perfect: "Perfeito",
        excellent: "Excelente",
        veryGood: "Muito Bom",
        good: "Bom",
        fair: "Razoável"
      },
      brandMatch: "Match {brand}"
    },

    markerRecommendations: {
      header: "Matches de Marcadores {brand}",
      summary: {
        excellent: {
          icon: "",
          message: "Encontrámos correspondências de cor excelentes!",
          description: "Estes marcadores estão muito próximos das cores da tua imagem"
        },
        good: {
          icon: "",
          message: "Encontrámos boas correspondências de cor!",
          description: "Estes marcadores devem funcionar bem para o teu trabalho"
        },
        ok: {
          icon: "",
          message: "Melhores correspondências disponíveis",
          description: "Considera misturar cores ou usar outra marca para matches mais próximos"
        }
      },
      stats: {
        excellent: "Excelente",
        good: "Bom",
        fair: "Razoável"
      },
      actions: {
        useBestEight: "Usar as 8 Melhores Cores",
        useTopQualityOnly: "Usar Apenas as de Maior Qualidade"
      },
      accuracyTitle: "Comparação de Precisão de Cor",
      matchConfidence: "Confiança do match: {percent}%",
      allRecommended: "Todos os Marcadores Recomendados ({count})",
      deltaETitle: "Sobre os valores Delta E (ΔE):",
      deltaE: {
        excellentTitle: "• ΔE 0-5: Matches excelentes",
        excellentDesc: "Cores praticamente indistinguíveis",
        veryGoodTitle: "• ΔE 5-12: Matches muito bons",
        veryGoodDesc: "Diferenças pequenas, ótimas para arte",
        goodTitle: "• ΔE 12-25: Matches bons",
        goodDesc: "Diferenças notórias mas aceitáveis",
        fairTitle: "• ΔE 25+: Matches razoáveis",
        fairDesc: "Diferenças significativas — considera alternativas"
      }
    },

    ohuhuCodes: {
      label: "Visualização de Códigos Ohuhu",
      options: {
        both: "Ambos os Códigos",
        old: "Apenas Antigo",
        new: "Apenas Novo"
      }
    },

    paletteSlots: {
      title: "A Tua Coleção de Cores",
      selected: "Marcadores selecionados: {count}",
      emptyHint: "Clica nas cores da imagem para começares a construir a tua paleta",
      maxHint: "Uau! Chegaste ao máximo de 20 cores. Que paleta incrível!"
    },

    sharePaletteModal: {
      title: "Partilha Esta Paleta",
      subtitle: "Copia e partilha este código com amigos ou nas redes sociais",
      urlCode: "URL Partilhável",
      copyUrl: "Copiar URL",
      copiedUrl: "URL Copiado",
      paletteCode: "Código da Paleta",
      copy: "Copiar código",
      copied: "Copiado",
      closeAria: "Fechar",
      imageReference: "Imagem da Paleta",
      downloadImage: "Guardar Imagem da Paleta",
      generatingImage: "A gerar imagem...",
      imageDescription: "Guarda uma imagem da palete compacta e um código QR.",
      imageError: "Erro ao gerar imagem. Tenta novamente.",
      noPaletteError: "Precisas de uma paleta antes de guardar a imagem.",
      qrHint: "Lê este QR para carregar a paleta"
    },

    similarSubfilter: {
      title: "Base de Cor",
      subtitle: "Escolhe uma família de cor para tons semelhantes",
      options: {
        allHues: "Todos os Tons",
        greenish: "Esverdeados",
        yellowish: "Amarelados",
        reddish: "Avermelhados",
        blueish: "Azulados",
        purplish: "Arroxeados"
      },
      descriptions: {
        allHues: "Quaisquer cores harmoniosas",
        greenish: "Tons de natureza e floresta",
        yellowish: "Tons quentes e solares",
        reddish: "Tons passionais e fortes",
        blueish: "Tons de oceano e céu",
        purplish: "Tons místicos e nobres"
      }
    },
  },
};

const I18nContext = createContext({
  language: LANG.EN,
  setLanguage: () => {},
  t: (key) => key,
});

export function I18nProvider({ initialLanguage = LANG.EN, children }) {
  const [language, setLanguage] = useState(initialLanguage);

  const t = useMemo(() => {
    // função de lookup tipo "nav.random" with variable replacement support
    return (key, vars) => {
      const dict = DICTS[language] || DICTS[LANG.EN];
      let text = key.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : null), dict) ?? key;

      // Replace variables like {count}, {max}, {name} etc.
      if (vars && typeof text === 'string') {
        Object.keys(vars).forEach(varKey => {
          text = text.replace(new RegExp(`\\{${varKey}\\}`, 'g'), vars[varKey]);
        });
      }

      return text;
    };
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
